/**
 * Searching the bundled food database.
 *
 * The bundled data is USDA's, which names things in a filing-cabinet style
 * ("Egg, whole, raw, fresh") rather than the way a person types them ("eggs").
 * Ranking therefore has to combine two things:
 *
 *   - query-independent genericness, precomputed at build time as `r`; and
 *   - query-dependent match quality, computed here.
 *
 * The decisive query-dependent signal is WHERE the query matches. USDA puts
 * the head noun first, so "Pepper, banana, raw" is a pepper and "Bananas, raw"
 * is a banana even though both contain the word. Matching the first token is
 * worth far more than matching a later one.
 */

import { FOOD_ALIASES, aliasKey } from './foodaliases';

export interface CoreFood {
  /** Stable id. */
  i: string;
  /** Display name. */
  n: string;
  /** Per 100 g. */
  k: number; p: number; c: number; f: number;
  fi?: number; s?: number;
  /** Household portions as [label, grams for one]. */
  po?: [string, number][];
  /** Search tokens. */
  q: string[];
  /** Genericness; lower is more generic. */
  r: number;
}

export interface FoodMatch {
  food: CoreFood;
  score: number;
}

/**
 * How people say it -> how USDA files it.
 *
 * USDA has no entry called "toast"; it has bread, toasted. It has no "soda";
 * it has carbonated beverages. This table is deliberately short and covers
 * only words whose everyday meaning differs from USDA's vocabulary — it is
 * not a general thesaurus, because every entry is a chance to mis-resolve
 * something the user actually meant literally.
 */
const SYNONYMS: Record<string, string[]> = {
  toast: ['bread', 'toasted'],
  coffee: ['coffee', 'brewed'],
  soda: ['carbonated', 'beverage'],
  pop: ['carbonated', 'beverage'],
  crisps: ['potato', 'chips'],
  mince: ['ground', 'beef'],
  courgette: ['squash', 'zucchini'],
  aubergine: ['eggplant'],
  rocket: ['arugula'],
  prawns: ['shrimp'],
  yoghurt: ['yogurt'],
};

/** Apply the synonym table to a tokenized query. */
export function expandSynonyms(tokens: string[]): string[] {
  const out: string[] = [];
  for (const t of tokens) {
    const mapped = SYNONYMS[t];
    if (mapped) out.push(...mapped);
    else out.push(t);
  }
  return [...new Set(out)];
}

/** Split a query into comparable tokens. */
export function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 1);
}

/** Very small plural/singular fold, enough for food nouns. */
export function stem(token: string): string {
  if (token.length > 3 && token.endsWith('ies')) return `${token.slice(0, -3)}y`;
  if (token.length > 3 && token.endsWith('es') && /(sh|ch|ss|x)es$/.test(token)) return token.slice(0, -2);
  if (token.length > 2 && token.endsWith('s') && !token.endsWith('ss')) return token.slice(0, -1);
  return token;
}

/**
 * USDA files many foods under a category word rather than their own name:
 * almonds are "Nuts, almonds, raw" and brewed coffee is "Beverages, coffee".
 * For these the first segment is a filing label, not the head noun, so the
 * second segment has to count as head too -- otherwise "almonds" loses to
 * "Almond butter", whose name genuinely starts with the word.
 */
const CATEGORY_HEADS = new Set([
  'nuts', 'seeds', 'beverages', 'beverage', 'cereals', 'cereal', 'snacks',
  'candies', 'soup', 'soups', 'oil', 'oils', 'fish', 'spices', 'sauce',
  'sauces', 'alcoholic', 'syrups', 'toppings', 'gravy', 'crustaceans',
  'mollusks', 'game', 'formulated', 'restaurant', 'fast', 'salad',
  'dressing', 'leavening', 'vegetables', 'fruit', 'fruits',
]);

function headTokens(name: string): string[] {
  const segments = name.split(',');
  const head = tokenize(segments[0] ?? '');
  if (head.length && CATEGORY_HEADS.has(head[0]!) && segments[1]) {
    // Additive, never replacing: "Cheese, cheddar" keeps cheese as a head
    // token while gaining cheddar.
    return [...head, ...tokenize(segments[1])];
  }
  return head;
}

/**
 * Score one food against a tokenized query. Higher is better; 0 means no match.
 *
 * Unmatched query tokens are penalised rather than disqualifying, because real
 * phrasing carries adjectives USDA never uses: nothing in the database is
 * named "black coffee", but "Coffee, brewed" is plainly what was meant. The
 * penalty is large enough that a food matching only a secondary token loses to
 * one matching the head noun -- "Turkey, breast" does not survive a search for
 * "chicken breast" -- without rejecting the query outright.
 */
export function scoreFood(food: CoreFood, queryTokens: string[]): number {
  if (!queryTokens.length) return 0;

  const tokens = food.q.map(stem);
  const head = headTokens(food.n).map(stem);
  let score = 0;
  let matched = 0;

  for (const raw of queryTokens) {
    const q = stem(raw);
    if (head.includes(q)) { score += 100; matched++; }
    else if (tokens.includes(q)) { score += 40; matched++; }
    else if (q.length >= 3 && tokens.some((t) => t.startsWith(q))) { score += 18; matched++; }
    else score -= 45;
  }
  if (matched === 0) return 0;

  // A head that is exactly the query is the ideal: "Eggs, raw" for "eggs".
  if (head.length === queryTokens.length && queryTokens.every((t) => head.includes(stem(t)))) {
    score += 50;
  }
  // Reward tight matches: a name that is mostly the query beats a long one
  // that merely contains it.
  score += Math.max(0, 40 - food.n.length * 0.3);
  // Genericness, precomputed at build time.
  score -= food.r * 0.6;
  return score > 0 ? score : 0;
}

/**
 * Best matches for a free-text query, highest score first.
 * A curated alias, when one matches the whole phrase, is pinned to the top;
 * the ranked results follow as alternatives.
 */
export function searchFoods(foods: CoreFood[], query: string, limit = 20): FoodMatch[] {
  const tokens = expandSynonyms(tokenize(query));
  if (!tokens.length) return [];
  const out: FoodMatch[] = [];
  for (const food of foods) {
    const score = scoreFood(food, tokens);
    if (score > 0) out.push({ food, score });
  }
  out.sort((a, b) => b.score - a.score || a.food.n.localeCompare(b.food.n));

  const aliasName = FOOD_ALIASES[aliasKey(query)];
  if (aliasName) {
    const pinned = foods.find((f) => f.n === aliasName);
    if (pinned) {
      const top = out[0]?.score ?? 0;
      return [{ food: pinned, score: top + 1000 }, ...out.filter((m) => m.food.i !== pinned.i)].slice(0, limit);
    }
  }
  return out.slice(0, limit);
}

/** The single best match, or null. */
export function bestMatch(foods: CoreFood[], query: string): CoreFood | null {
  return searchFoods(foods, query, 1)[0]?.food ?? null;
}

/** Abbreviations -> the spellings USDA uses in portion labels. */
const PORTION_ALIASES: Record<string, string[]> = {
  tbsp: ['tbsp', 'tablespoon'],
  tsp: ['tsp', 'teaspoon'],
  cup: ['cup'],
  slice: ['slice'],
  piece: ['piece'],
  scoop: ['scoop'],
  serving: ['serving'],
  can: ['can'],
  bottle: ['bottle'],
  large: ['large'],
  medium: ['medium'],
  small: ['small'],
};

/**
 * Grams for a stated amount of a food.
 * Returns null when the unit cannot be resolved -- the caller should then ask
 * rather than guess, because a wrong gram weight is a wrong macro count.
 */
export function gramsFor(food: CoreFood, qty: number, unit: string | null): number | null {
  if (!Number.isFinite(qty) || qty < 0) return null;
  const u = (unit ?? '').toLowerCase().trim();

  if (u === 'g' || u === 'gram' || u === 'grams') return qty;
  if (u === 'kg') return qty * 1000;
  if (u === 'oz' || u === 'ounce' || u === 'ounces') return qty * 28.349523125;
  if (u === 'lb' || u === 'lbs' || u === 'pound' || u === 'pounds') return qty * 453.59237;

  const portions = food.po ?? [];
  if (u) {
    // USDA spells units out ("tablespoon"); people abbreviate ("tbsp").
    const wanted = PORTION_ALIASES[u] ?? [u];
    for (const w of wanted) {
      const exact = portions.find((p) => p[0].toLowerCase() === w);
      if (exact) return qty * exact[1];
    }
    for (const w of wanted) {
      const partial = portions.find((p) => {
        const label = p[0].toLowerCase();
        return label.startsWith(w) || label.includes(` ${w}`) || w.includes(label);
      });
      if (partial) return qty * partial[1];
    }
    // ml is only safe for water-like foods; refuse rather than assume density.
    if (u === 'ml' || u === 'l') return null;
  }

  // No unit given ("2 eggs"): use the first household portion as "one of these".
  if (!u && portions.length) return qty * portions[0]![1];
  return null;
}
